<template>
  <div class="tcv2-page" ref="tcv2PageRoot" :style="pageStyle">
    <!-- ===== Toolbar ===== -->
    <v-toolbar color="primary" density="comfortable" class="tcv2-toolbar text-white">
      <v-btn variant="text" prepend-icon="mdi-arrow-left" data-testid="tcv2-back" @click="goBack">
        Tax Configuration V2
      </v-btn>
      <v-toolbar-title class="tcv2-title">
        Tax Calculate V2 — รายการฟังก์ชัน
        <span v-if="versionCode" class="tcv2-sub">· {{ versionCode }}</span>
      </v-toolbar-title>
      <v-spacer />
      <v-chip
        v-if="isActiveVersion"
        size="small"
        color="white"
        variant="elevated"
        class="mr-2 tcv2-text-primary"
        data-testid="tcv2-active"
      >
        <v-icon start size="14">mdi-circle-slice-8</v-icon> ACTIVE
      </v-chip>
      <v-chip v-if="!loading && !loadError" size="small" color="white" variant="tonal" class="mr-2" data-testid="tcv2-count">
        {{ functions.length }} ฟังก์ชัน
      </v-chip>
      <v-chip
        v-if="!loading && !loadError && attentionCount > 0"
        size="small"
        color="white"
        variant="tonal"
        class="mr-2"
        data-testid="tcv2-attention"
      >
        <v-icon start size="14">mdi-alert-outline</v-icon> {{ attentionCount }} ต้องตรวจสอบ
      </v-chip>
    </v-toolbar>

    <div v-if="loadError" class="tcv2-center">
      <v-icon size="42" color="error">mdi-alert-circle-outline</v-icon>
      <span class="mt-3 text-error" data-testid="tcv2-error">{{ loadError }}</span>
      <v-btn class="mt-4" variant="tonal" color="primary" data-testid="tcv2-retry" @click="loadAll">ลองอีกครั้ง</v-btn>
    </div>
    <div v-else-if="loading" class="tcv2-center">
      <v-progress-circular indeterminate color="primary" size="48" />
      <span class="mt-3 text-grey">กำลังโหลดฟังก์ชัน…</span>
    </div>

    <template v-else>
      <!-- ===== Version header ===== -->
      <div class="tcv2-head" data-testid="tcv2-head">
        <v-icon size="18" color="primary" class="mr-2">mdi-package-variant-closed</v-icon>
        <code class="tcv2-head-code">{{ versionCode || '—' }}</code>
        <span class="tcv2-head-desc">{{ version && version.description ? version.description : 'ไม่มีคำอธิบาย' }}</span>
      </div>

      <!-- ===== Actions ===== -->
      <div class="tcv2-actions">
        <v-btn
          size="small"
          color="primary"
          variant="flat"
          prepend-icon="mdi-plus"
          data-testid="tcv2-add"
          @click="openCreate"
        >สร้างฟังก์ชันใหม่</v-btn>
        <!-- Save history: every formula write snapshots the WHOLE calc version, so this is the way
             back to any earlier round (compare + restore live inside the dialog). -->
        <v-btn
          size="small"
          color="primary"
          variant="outlined"
          prepend-icon="mdi-history"
          data-testid="tcv2-history"
          :disabled="busy || calcVersionId == null"
          @click="openHistory"
        >
          ประวัติการบันทึก
          <v-tooltip activator="parent" location="top">ดูรอบการบันทึกย้อนหลัง เทียบ และกู้คืน</v-tooltip>
        </v-btn>
        <v-spacer />
        <span class="tcv2-hint">
          <v-icon size="14">mdi-information-outline</v-icon>
          ลูกศร ↑↓ = ลำดับที่แสดงเท่านั้น (ไม่มีผลต่อการคำนวณ) · การเรียง / ลบ / ทำสำเนา บันทึกทันที
        </span>
      </div>

      <!-- ===== Function list ===== -->
      <div class="tcv2-list" data-testid="tcv2-list">
        <v-alert v-if="!functions.length" type="info" variant="tonal" density="compact" class="mb-3" data-testid="tcv2-empty">
          ยังไม่มีฟังก์ชันในเวอร์ชันนี้ — กด “สร้างฟังก์ชันใหม่” เพื่อเริ่ม (ระบบจะสร้างโค้ดตัวอย่างที่คอมไพล์ผ่านให้ก่อน)
        </v-alert>

        <v-card
          v-for="(f, idx) in functions"
          :key="f.functionID"
          class="tcv2-row"
          variant="outlined"
          :data-testid="`tcv2-row-${idx}`"
          @click="editFunction(f)"
        >
          <div class="tcv2-row-main">
            <div class="tcv2-order" @click.stop>
              <v-btn
                icon
                size="x-small"
                variant="text"
                :disabled="idx === 0 || busy"
                :data-testid="`tcv2-up-${idx}`"
                @click="moveFunction(idx, -1)"
              >
                <v-icon size="18">mdi-chevron-up</v-icon>
                <v-tooltip activator="parent" location="top">ย้ายขึ้น (ลำดับที่แสดงเท่านั้น)</v-tooltip>
              </v-btn>
              <span class="tcv2-order-num">{{ idx + 1 }}</span>
              <v-btn
                icon
                size="x-small"
                variant="text"
                :disabled="idx === functions.length - 1 || busy"
                :data-testid="`tcv2-down-${idx}`"
                @click="moveFunction(idx, 1)"
              >
                <v-icon size="18">mdi-chevron-down</v-icon>
                <v-tooltip activator="parent" location="top">ย้ายลง (ลำดับที่แสดงเท่านั้น)</v-tooltip>
              </v-btn>
            </div>

            <div class="tcv2-row-body">
              <div class="tcv2-row-head">
                <v-icon size="16" color="primary" class="mr-1">mdi-language-csharp</v-icon>
                <code class="tcv2-code" :data-testid="`tcv2-code-${idx}`">{{ f.functionCode }}</code>
                <span class="tcv2-name" :data-testid="`tcv2-name-${idx}`">{{ f.name || '—' }}</span>

                <!-- health: a C# body cannot be shown inline the way V1 showed `= expression`, so the
                     compile outcome IS the row's readable state. A STALE row's cached status describes an
                     older source — showing a green tick there would be a lie, so it becomes amber. -->
                <v-chip
                  v-if="f.isStale"
                  size="x-small"
                  variant="tonal"
                  color="warning"
                  class="ml-2"
                  :data-testid="`tcv2-stale-${idx}`"
                >
                  <v-icon start size="13">mdi-refresh-alert</v-icon> ต้องคอมไพล์ใหม่
                  <v-tooltip activator="parent" location="top">
                    ซอร์สถูกแก้หลังคอมไพล์ครั้งล่าสุด — สถานะที่เก็บไว้เป็นของโค้ดเวอร์ชันเก่า
                  </v-tooltip>
                </v-chip>
                <v-chip
                  size="x-small"
                  variant="tonal"
                  :color="f.isStale ? 'grey' : healthOf(f).color"
                  class="ml-1"
                  :data-testid="`tcv2-health-${idx}`"
                >
                  <v-icon start size="13">{{ healthOf(f).icon }}</v-icon>
                  {{ f.isStale ? `สถานะที่บันทึกไว้: ${healthOf(f).text}` : healthOf(f).text }}
                </v-chip>

                <v-chip
                  v-if="f.fieldKey"
                  size="x-small"
                  variant="tonal"
                  color="primary"
                  class="ml-1"
                  :data-testid="`tcv2-fieldkey-${idx}`"
                >
                  <v-icon start size="13">mdi-key-variant</v-icon> {{ f.fieldKey }}
                </v-chip>
                <!-- The chip above is the field this formula WRITES; this one is how many of its parameters
                     already know which fields to READ. It hides itself entirely until something is bound, so
                     rows that predate parameter binding stay exactly as quiet as they are today. -->
                <v-chip
                  v-if="bindingSummaryOf(f)"
                  size="x-small"
                  variant="tonal"
                  :color="bindingSummaryOf(f).color"
                  class="ml-1"
                  :data-testid="`tcv2-bindings-${idx}`"
                >
                  <v-icon start size="13">mdi-link-variant</v-icon>
                  ผูกค่า {{ bindingSummaryOf(f).bound }}/{{ bindingSummaryOf(f).total }}
                  <v-tooltip activator="parent" location="top">{{ bindingSummaryOf(f).hint }}</v-tooltip>
                </v-chip>
                <v-chip v-if="!f.isActive" size="x-small" variant="tonal" color="warning" class="ml-1">ปิดใช้งาน</v-chip>

                <span v-if="f.description" class="tcv2-desc">{{ f.description }}</span>
              </div>

              <!-- The SIGNATURE is what replaces V1's inline `= expression`: it is the only part of a C#
                   body that is both short enough for a row and precise enough to act on. -->
              <div class="tcv2-row-sig">
                <span class="tcv2-sig-mark">ƒ</span>
                <code class="tcv2-sig" :data-testid="`tcv2-signature-${idx}`">{{ signatureOf(f) }}</code>
              </div>

              <div v-if="f.compiledDate" class="tcv2-row-meta">
                <v-icon size="13">mdi-clock-outline</v-icon>
                คอมไพล์ล่าสุด {{ formatDate(f.compiledDate) }}
                <span v-if="f.updatedBy" class="ml-2">· แก้ไขโดย {{ f.updatedBy }}</span>
              </div>
            </div>

            <div class="tcv2-row-actions" @click.stop>
              <v-btn
                icon
                size="small"
                variant="text"
                color="primary"
                :data-testid="`tcv2-run-toggle-${idx}`"
                @click="toggleRun(f)"
              >
                <v-icon>mdi-play-circle-outline</v-icon>
                <v-tooltip activator="parent" location="top">ทดลองรันฟังก์ชันนี้</v-tooltip>
              </v-btn>
              <v-btn
                size="small"
                variant="tonal"
                color="primary"
                prepend-icon="mdi-code-braces"
                :data-testid="`tcv2-edit-${idx}`"
                @click="editFunction(f)"
              >เปิดแก้ไข</v-btn>
              <v-btn icon size="small" variant="text" :disabled="busy" :data-testid="`tcv2-dup-${idx}`" @click="duplicateFunction(f)">
                <v-icon>mdi-content-copy</v-icon>
                <v-tooltip activator="parent" location="top">ทำสำเนา</v-tooltip>
              </v-btn>
              <v-btn
                icon
                size="small"
                variant="text"
                color="error"
                :disabled="busy"
                :data-testid="`tcv2-del-${idx}`"
                @click="askDelete(f)"
              >
                <v-icon>mdi-delete-outline</v-icon>
                <v-tooltip activator="parent" location="top">ลบ</v-tooltip>
              </v-btn>
            </div>
          </div>

          <!-- ===== inline test-run panel ===== -->
          <v-expand-transition>
            <div v-if="panels[f.functionID] && panels[f.functionID].open" class="tcv2-run" @click.stop>
              <div class="tcv2-run-head">
                <v-icon size="16" class="mr-1" color="primary">mdi-flask-outline</v-icon>
                <strong>ทดลองรัน “{{ f.functionCode }}”</strong>
                <span class="tcv2-run-hint">— ช่องกรอกสร้างจากพารามิเตอร์ของเมธอด Run</span>
                <v-spacer />
                <v-btn icon size="x-small" variant="text" :data-testid="`tcv2-run-close-${idx}`" @click="closeRun(f)">
                  <v-icon size="18">mdi-close</v-icon>
                  <v-tooltip activator="parent" location="top">ปิดแผงทดลองรัน</v-tooltip>
                </v-btn>
              </div>

              <v-alert
                v-if="!panels[f.functionID].fields.length"
                type="info"
                variant="tonal"
                density="compact"
                class="mb-2"
              >
                {{
                  f.compileStatus === 'Compiled'
                    ? 'ฟังก์ชันนี้ไม่มีพารามิเตอร์ — กดรันได้เลย'
                    : 'ยังไม่ทราบพารามิเตอร์ (ฟังก์ชันยังคอมไพล์ไม่ผ่าน) — กดรันเพื่อดูข้อผิดพลาดจากคอมไพเลอร์'
                }}
              </v-alert>

              <div class="tcv2-run-fields">
                <template v-for="fld in panels[f.functionID].fields" :key="fld.name">
                  <v-checkbox
                    v-if="fld.kind === 'boolean'"
                    v-model="panels[f.functionID].values[fld.name]"
                    :label="fieldLabel(fld)"
                    :error="isErrorParam(f, fld)"
                    density="compact"
                    hide-details
                    class="tcv2-run-check"
                    :data-testid="`tcv2-arg-${idx}-${fld.name}`"
                  />
                  <v-text-field
                    v-else
                    v-model="panels[f.functionID].values[fld.name]"
                    :type="fld.kind === 'number' ? 'number' : 'text'"
                    :label="fieldLabel(fld)"
                    :placeholder="fld.placeholder"
                    persistent-placeholder
                    :error="isErrorParam(f, fld)"
                    density="compact"
                    variant="outlined"
                    hide-details
                    class="tcv2-run-field"
                    :data-testid="`tcv2-arg-${idx}-${fld.name}`"
                  />
                </template>
                <v-btn
                  color="primary"
                  size="small"
                  variant="flat"
                  prepend-icon="mdi-play"
                  :loading="panels[f.functionID].loading"
                  :data-testid="`tcv2-run-${idx}`"
                  @click="runOne(f)"
                >รัน</v-btn>
              </div>

              <!-- a failed run is a 200 with ok:false — it is a normal outcome, not a transport error -->
              <v-alert
                v-if="panels[f.functionID].error"
                type="error"
                variant="tonal"
                density="compact"
                class="mt-2"
                :data-testid="`tcv2-run-error-${idx}`"
              >
                <div><strong>{{ errorKindLabel(panels[f.functionID].error.errorKind) }}</strong></div>
                <div>{{ panels[f.functionID].error.errorMessage || 'รันไม่สำเร็จ' }}</div>
                <div v-if="panels[f.functionID].error.errorParameter" class="tcv2-run-errparam">
                  พารามิเตอร์: <code>{{ panels[f.functionID].error.errorParameter }}</code>
                </div>
                <div v-for="(e, ei) in panels[f.functionID].error.errors || []" :key="`re-${ei}`" class="tcv2-run-diag">
                  <code v-if="e.id">{{ e.id }}</code>
                  <span v-if="e.line">บรรทัด {{ e.line }}<span v-if="e.column">:{{ e.column }}</span> —</span>
                  {{ e.message }}
                </div>
                <div v-if="panels[f.functionID].error.errorDetail" class="tcv2-run-detail">
                  {{ panels[f.functionID].error.errorDetail }}
                </div>
              </v-alert>

              <div v-if="panels[f.functionID].result" class="tcv2-run-result" :data-testid="`tcv2-result-${idx}`">
                <div class="tcv2-run-value">
                  <span class="tcv2-run-value-label">ผลลัพธ์ =</span>
                  <strong>{{ resultText(panels[f.functionID].result) }}</strong>
                </div>
                <div class="tcv2-run-stats">
                  <v-icon size="13">mdi-timer-outline</v-icon>
                  {{ panels[f.functionID].result.elapsedMs }} ms
                  <span class="ml-2">
                    · {{ panels[f.functionID].result.compiled ? 'คอมไพล์ใหม่รอบนี้' : 'ใช้แอสเซมบลีที่แคชไว้' }}
                  </span>
                  <span v-if="panels[f.functionID].result.entryPoint" class="ml-2">
                    · entry point <code>{{ panels[f.functionID].result.entryPoint }}</code>
                  </span>
                </div>
              </div>
            </div>
          </v-expand-transition>
        </v-card>
      </div>
    </template>

    <!-- ===== create dialog ===== -->
    <v-dialog v-model="createDialog.open" max-width="520" data-testid="tcv2-create-dialog">
      <v-card>
        <v-card-title class="text-subtitle-1">สร้างฟังก์ชันใหม่</v-card-title>
        <v-card-text>
          <p class="text-body-2 mb-4 text-medium-emphasis">
            ระบบจะสร้างโค้ด C# ตัวอย่างที่คอมไพล์ผ่านให้ก่อน แล้วเปิดหน้าแก้ไขให้ทันที
          </p>
          <v-text-field
            v-model="createDialog.functionCode"
            label="รหัสฟังก์ชัน (FunctionCode) *"
            placeholder="เช่น ExpenseDeduction40_5"
            hint="รหัสนี้คือ “ชื่อคลาส C#” ในซอร์สโค้ด — ใช้ได้เฉพาะ A-Z a-z 0-9 _ และห้ามขึ้นต้นด้วยตัวเลข"
            persistent-hint
            density="compact"
            variant="outlined"
            hide-details="auto"
            class="mb-3"
            :rules="codeRules"
            data-testid="tcv2-create-code"
          />
          <v-text-field
            v-model="createDialog.name"
            label="ชื่อฟังก์ชัน (ภาษาไทย)"
            placeholder="เช่น ค่าใช้จ่ายเหมา ม.40(5)"
            density="compact"
            variant="outlined"
            hide-details
            class="mb-3"
            data-testid="tcv2-create-name"
          />
          <v-text-field
            v-model="createDialog.description"
            label="คำอธิบาย"
            density="compact"
            variant="outlined"
            hide-details
            class="mb-3"
            data-testid="tcv2-create-desc"
          />
          <!-- OUTPUT binding — see the duplicate() comment below for why this is NOT parameterBindings.
               A COMBOBOX, never an autocomplete: the registry only holds fields that are ASKED of the
               customer, so a derived output key (the usual case here) exists nowhere in the list yet.
               Restricting the input to the list would make those keys unpickable. -->
          <v-combobox
            :model-value="createDialog.fieldKey"
            :items="fieldItems"
            item-title="title"
            item-value="value"
            :return-object="false"
            :custom-filter="filterFieldItem"
            :loading="loadingFieldRules"
            :no-data-text="fieldNoDataText"
            label="FieldKey ที่ผูก (ถ้ามี)"
            placeholder="ค้นจาก FieldKey หรือ Label — หรือพิมพ์คีย์ใหม่ได้เลย"
            prepend-inner-icon="mdi-key-variant"
            :hint="createFieldKeyHint"
            persistent-hint
            density="compact"
            variant="outlined"
            hide-details="auto"
            clearable
            data-testid="tcv2-create-fieldkey"
            @update:model-value="setCreateFieldKey"
          >
            <template #item="{ props: itemProps, item }">
              <v-list-item v-bind="itemProps" :title="item.raw.fieldKey">
                <template #subtitle>
                  <span class="tcv2-opt-label">{{ item.raw.label || '—' }}</span>
                  <v-chip v-if="item.raw.valueType" size="x-small" variant="tonal" class="ml-1">
                    {{ item.raw.valueType }}
                  </v-chip>
                </template>
              </v-list-item>
            </template>
          </v-combobox>
          <!-- Nothing in the DB stops two formulas writing the same field, and the one that runs later
               simply overwrites the other — so the collision is said out loud here, before it is saved. -->
          <div v-if="fieldKeyOwner" class="tcv2-create-warn" data-testid="tcv2-create-fieldkey-dup">
            <v-icon size="13">mdi-alert-outline</v-icon>
            <span>
              FieldKey นี้มีสูตร <code>{{ fieldKeyOwner }}</code> ผูกไว้อยู่แล้ว —
              ทั้งสองสูตรจะเขียนลงฟิลด์เดียวกัน และสูตรที่รันทีหลังจะทับผลของอีกสูตร
            </span>
          </div>
        </v-card-text>
        <v-card-actions>
          <v-spacer />
          <v-btn variant="text" data-testid="tcv2-create-cancel" @click="createDialog.open = false">ยกเลิก</v-btn>
          <v-btn
            color="primary"
            variant="flat"
            :loading="creating"
            :disabled="!isCreateCodeValid"
            data-testid="tcv2-create-submit"
            @click="confirmCreate"
          >สร้างและเปิดแก้ไข</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- ===== delete confirm (blast radius computed before it opens) ===== -->
    <v-dialog v-model="deleteDialog.open" max-width="460" data-testid="tcv2-delete-dialog">
      <v-card>
        <v-card-title>ลบฟังก์ชัน</v-card-title>
        <v-card-text>
          ลบฟังก์ชัน <code>{{ deleteDialog.fn && deleteDialog.fn.functionCode }}</code> ออกจากเวอร์ชันนี้?
          ซอร์สโค้ดจะหายไปด้วยและกู้คืนไม่ได้
          <div v-if="deleteDialog.impacts.length" class="mt-3">
            <v-alert type="warning" variant="tonal" density="compact" data-testid="tcv2-delete-impact">
              <div v-for="(m, mi) in deleteDialog.impacts" :key="`imp-${mi}`">• {{ m }}</div>
            </v-alert>
          </div>
        </v-card-text>
        <v-card-actions>
          <v-spacer />
          <v-btn variant="text" data-testid="tcv2-delete-cancel" @click="deleteDialog.open = false">ยกเลิก</v-btn>
          <v-btn color="error" variant="tonal" :loading="busy" data-testid="tcv2-delete-confirm" @click="confirmDelete">ลบ</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- Save history (list + compare + restore all live inside the dialog; the compare dialog is
         mounted by it, so the two always open against the same rows). -->
    <TaxCalcHistoryV2Dialog
      v-model="historyDialog"
      :calc-version-id="calcVersionId"
      :version-label="historyLabel"
      :version-active="isActiveVersion"
      @restored="onHistoryRestored"
    />

    <v-snackbar v-model="snackbar.show" :color="snackbar.color" location="bottom" timeout="3200">
      {{ snackbar.message }}
    </v-snackbar>
  </div>
</template>

<script>
/**
 * Tax Calculate V2 — FUNCTION LIST of one calc version.
 *
 * The direct analogue of V1's TaxCalculateList.vue (outlined v-card rows, fixed-breadcrumb inset,
 * snackbar convention, delete dialog that states its blast radius). The one structural difference is
 * what a row can SHOW: a V1 formula is a one-line `= expression`, a V2 formula is a C# body. So the
 * row shows the SIGNATURE (returnType + parameters, both produced by the compiler) plus the compile
 * health, and the body itself lives one level down in the editor.
 *
 * Contract notes that shape this file:
 *  • compileStatus + isStale are the health signal. `isStale` means the cached status belongs to an
 *    OLDER source, so it is rendered amber ("ต้องคอมไพล์ใหม่") and the cached status is demoted to a
 *    grey "สถานะที่บันทึกไว้: …" chip — never a green tick.
 *  • validate/run answer HTTP 200 with `ok:false` on compile/binding/timeout/runtime failure. Only a
 *    transport failure throws, so `runOne` branches on `res.ok`, not on catch.
 *  • `parameters` is the run panel's field list; `required` means "no default", and a missing required
 *    argument comes back as errorKind 'binding' + errorParameter — which this page paints onto that
 *    exact input.
 */
import { taxCalculateV2Api } from '../services/taxCalculateV2Api'
import { taxConfigV2Api } from '../services/taxConfigV2Api'
import { parseUtcDate, THAI_TIME_ZONE } from '../utils/thaiTime.js'
import TaxCalcHistoryV2Dialog from '../components/TaxCalcHistoryV2Dialog.vue'

/** Legal C# identifier — the same shape the backend's NormalizeCode enforces. */
const IDENTIFIER_RE = /^[A-Za-z_][A-Za-z0-9_]*$/

/** CLR type names (what the compiler reports: `Decimal`, `Decimal?`, `Boolean`) → C# keywords. */
const CS_TYPE_KEYWORDS = {
  boolean: 'bool',
  byte: 'byte',
  sbyte: 'sbyte',
  char: 'char',
  decimal: 'decimal',
  double: 'double',
  single: 'float',
  int16: 'short',
  int32: 'int',
  int64: 'long',
  uint16: 'ushort',
  uint32: 'uint',
  uint64: 'ulong',
  object: 'object',
  string: 'string',
  void: 'void',
}

export default {
  name: 'TaxCalculateV2List',
  components: { TaxCalcHistoryV2Dialog },
  data() {
    return {
      loading: true,
      loadError: '',
      busy: false,
      creating: false,
      configVersionId: this.$route.params.configVersionId,
      calcVersionId: (() => {
        const n = Number(this.$route.params.calcVersionId)
        return Number.isFinite(n) ? n : null
      })(),
      version: null,
      functions: [],
      // Inline test-run panels, keyed by functionID so entered values survive a re-render/reload.
      panels: {},
      createDialog: { open: false, functionCode: '', name: '', description: '', fieldKey: '' },
      deleteDialog: { open: false, fn: null, impacts: [] },
      // FieldKey registry of THIS config version — fetched lazily when the create dialog opens (the list
      // itself never needs it) and kept for the rest of the page's life; see loadFieldRules.
      fieldRules: [],
      fieldRulesLoaded: false,
      loadingFieldRules: false,
      fieldRulesError: '',
      // The app-shell breadcrumb bar is position:fixed (NavBar.vue .fixed-breadcrumbs, top:64px) and
      // reserves no space, so this page measures it and pads below — same as V1's list/editor.
      topPad: 48,
      // Save-history dialog. It owns its own errors (v-alert inside the box) — this page only owns
      // the snackbar, and only speaks after a restore actually committed.
      historyDialog: false,
      snackbar: { show: false, color: 'success', message: '' },
    }
  },
  computed: {
    pageStyle() {
      return { paddingTop: this.topPad + 'px' }
    },
    versionCode() {
      return (this.version && this.version.versionCode) || ''
    },
    isActiveVersion() {
      return !!(this.version && this.version.isActive)
    },
    /** Subtitle of the history dialog — which calc version the listed rounds belong to. */
    historyLabel() {
      const code = this.versionCode || `#${this.calcVersionId}`
      const desc = (this.version && this.version.description) || ''
      return desc ? `${code} — ${desc}` : code
    },
    /** Rows whose cached compile status is unusable as-is: an Error, or a status about older source. */
    attentionCount() {
      return this.functions.filter((f) => f.isStale || f.compileStatus === 'Error').length
    },
    /** Lower-cased function codes in this version — uniqueness is per calc version, server-side too. */
    codeSet() {
      const s = new Set()
      this.functions.forEach((f) => {
        const c = String(f.functionCode || '').trim().toLowerCase()
        if (c) s.add(c)
      })
      return s
    },
    codeRules() {
      return [
        (v) => !!String(v || '').trim() || 'ต้องระบุรหัสฟังก์ชัน',
        (v) => IDENTIFIER_RE.test(String(v || '').trim()) || 'ต้องเป็นชื่อคลาส C# ที่ถูกต้อง — ใช้ได้เฉพาะ A-Z a-z 0-9 _ และห้ามขึ้นต้นด้วยตัวเลข',
        (v) => String(v || '').trim().length <= 80 || 'รหัสฟังก์ชันยาวเกิน 80 ตัวอักษร',
        (v) => !this.codeSet.has(String(v || '').trim().toLowerCase()) || 'รหัสนี้ถูกใช้แล้วในเวอร์ชันนี้',
      ]
    },
    isCreateCodeValid() {
      const code = String(this.createDialog.functionCode || '').trim()
      return IDENTIFIER_RE.test(code) && code.length <= 80 && !this.codeSet.has(code.toLowerCase())
    },

    // ── FieldKey picker (create dialog) ──
    /**
     * Combobox items. `title` is the FieldKey ALONE on purpose: a combobox writes the chosen item's title
     * straight into its own text box, so a decorated title ("KEY — label") would become the value the
     * author sees and edits. Label + ValueType are rendered by the item slot instead, and
     * `filterFieldItem` is what keeps the Thai label searchable.
     */
    fieldItems() {
      return this.fieldRules.map((r) => ({
        title: r.fieldKey,
        value: r.fieldKey,
        fieldKey: r.fieldKey,
        label: r.label,
        valueType: r.valueType,
      }))
    },
    /** Only a registry we actually received can prove a key is new — a failed/empty fetch must not. */
    registryReady() {
      return !this.loadingFieldRules && !this.fieldRulesError && this.fieldRules.length > 0
    },
    fieldNoDataText() {
      if (this.loadingFieldRules) return 'กำลังโหลดรายการ FieldKey…'
      if (this.fieldRulesError) return 'โหลดรายการ FieldKey ไม่สำเร็จ — พิมพ์รหัสเองได้'
      return 'ไม่พบ FieldKey ที่ค้นหา — กด Enter เพื่อใช้เป็นคีย์ใหม่'
    },
    /** The exact string confirmCreate would send. */
    createFieldKey() {
      return String(this.createDialog.fieldKey || '').trim()
    },
    /**
     * A key the registry does not have. NOT an error: the registry only lists fields the customer is
     * asked for, so a calculated output key is expected to be absent until something else declares it.
     */
    isNewFieldKey() {
      const key = this.createFieldKey.toLowerCase()
      if (!key || !this.registryReady) return false
      // Case-insensitive: the backend resolver matches that way, so a case-only difference is not a new key.
      return !this.fieldRules.some((r) => r.fieldKey.toLowerCase() === key)
    },
    /** FunctionCode of a function in THIS version that already writes the same key ('' when none). */
    fieldKeyOwner() {
      const key = this.createFieldKey.toLowerCase()
      if (!key) return ''
      const hit = this.functions.find((f) => String(f.fieldKey || '').trim().toLowerCase() === key)
      return hit ? (hit.functionCode || '') : ''
    },
    createFieldKeyHint() {
      if (this.loadingFieldRules) return 'กำลังโหลดรายการ FieldKey จาก Tax Config V2…'
      if (this.fieldRulesError) return this.fieldRulesError
      if (this.isNewFieldKey) {
        return `“${this.createFieldKey}” ยังไม่มีใน Tax Config V2 — บันทึกได้ตามปกติ (ฟิลด์ที่เกิดจากการคำนวณมักยังไม่มีในรายการ)`
      }
      return 'รหัสฟิลด์ใน Tax Config V2 ที่ผลลัพธ์ของฟังก์ชันนี้ไปลง — เลือกจากรายการ หรือพิมพ์คีย์ใหม่ก็ได้'
    },
  },
  created() {
    this.loadAll()
  },
  mounted() {
    this._onResize = () => this.updateLayoutInsets()
    window.addEventListener('resize', this._onResize)
    // Measure after the breadcrumb has laid out (it lives in a sibling app-shell component).
    this.$nextTick(() => this.updateLayoutInsets())
  },
  beforeUnmount() {
    if (this._onResize) window.removeEventListener('resize', this._onResize)
  },
  methods: {
    /** Pad this page below the app-shell's fixed breadcrumb bar so the toolbar isn't overlapped. */
    updateLayoutInsets() {
      const root = this.$refs.tcv2PageRoot
      if (!root) return
      // getBoundingClientRect().top is the border-box top — unaffected by our own paddingTop.
      const naturalTop = root.getBoundingClientRect().top
      let breadcrumbBottom = naturalTop
      const bc = document.querySelector('.fixed-breadcrumbs')
      if (bc) {
        const r = bc.getBoundingClientRect()
        if (r.height > 0) breadcrumbBottom = r.bottom // skip when hidden (0-height)
      }
      const pad = Math.max(0, Math.round(breadcrumbBottom - naturalTop))
      if (pad !== this.topPad) this.topPad = pad
    },

    // ── load ────────────────────────────────────────────────────────────────
    async loadAll() {
      if (this.calcVersionId == null) {
        this.loading = false
        this.loadError = 'พารามิเตอร์ calcVersionId ไม่ถูกต้อง'
        return
      }
      this.loading = true
      this.loadError = ''
      try {
        const [version, functions] = await Promise.all([
          taxCalculateV2Api.getVersion(this.calcVersionId),
          taxCalculateV2Api.listFunctions(this.calcVersionId, true),
        ])
        if (!version) throw new Error('ไม่พบเวอร์ชันการคำนวณนี้')
        this.version = version
        this.functions = (Array.isArray(functions) ? functions : []).slice().sort(
          // sortOrder is the display order of THIS list only — run order comes from the ordered function list
          // of a CALCULATE question item, never from here. functionID breaks ties so the list never re-shuffles itself.
          (a, b) => (Number(a.sortOrder) || 0) - (Number(b.sortOrder) || 0) || (a.functionID - b.functionID),
        )
        this.syncPanels()
      } catch (err) {
        this.loadError = (err && err.message) ? err.message : 'โหลดข้อมูลไม่สำเร็จ'
      } finally {
        this.loading = false
        // Re-measure once content is in: the breadcrumb chain can change height after the version loads.
        this.$nextTick(() => this.updateLayoutInsets())
      }
    },

    // ── navigation ──────────────────────────────────────────────────────────
    /**
     * Named push with a literal-path fallback: the route table lives in router.js (a file this page
     * does not own), and vue-router throws synchronously on an unregistered NAME — a half-registered
     * table should degrade to a working link, not a crash.
     */
    navigate(name, params, path) {
      try {
        this.$router.push({ name, params })
      } catch (e) {
        this.$router.push(path)
      }
    },
    goBack() {
      // The calc-version list lives inside the Tax Configuration V2 tree — there is no
      // standalone calc-version route to return to.
      this.$router.push('/TaxConfigurationV2')
    },
    editFunction(f) {
      const params = {
        configVersionId: String(this.configVersionId),
        calcVersionId: String(this.calcVersionId),
        functionId: String(f.functionID),
      }
      this.navigate(
        'TaxCalcFunctionV2Edit',
        params,
        `/TaxCalculateV2/${params.configVersionId}/${params.calcVersionId}/function/${params.functionId}`,
      )
    },

    // ── row rendering helpers ───────────────────────────────────────────────
    /** `Decimal?` → `decimal?`, `List<String>` → left alone (generics stay CLR-shaped). */
    csType(type) {
      const raw = String(type == null ? '' : type).trim()
      if (!raw) return 'var'
      let base = raw
      let suffix = ''
      // Peel `?` / `[]` off the end so the keyword map sees a bare type name.
      for (;;) {
        if (base.endsWith('?')) { suffix = '?' + suffix; base = base.slice(0, -1); continue }
        if (base.endsWith('[]')) { suffix = '[]' + suffix; base = base.slice(0, -2); continue }
        break
      }
      const keyword = CS_TYPE_KEYWORDS[base.toLowerCase()]
      return (keyword || base) + suffix
    },
    /** A C#-looking literal for a parameter default, e.g. `0.5`, `"x"`, `true`, `null`. */
    literalOf(value) {
      if (value === null || value === undefined) return 'null'
      if (typeof value === 'string') return `"${value}"`
      if (typeof value === 'boolean') return value ? 'true' : 'false'
      if (typeof value === 'number') return String(value)
      try { return JSON.stringify(value) } catch (e) { return String(value) }
    },
    /**
     * `decimal Run(decimal income, decimal rate = 0.5)` — built from returnType + parameters, which is
     * everything the compiler knows about the entry point. The method name is always `Run`: the
     * compiler requires exactly one `public static Run` per function class.
     */
    signatureOf(f) {
      const params = Array.isArray(f.parameters) ? f.parameters : []
      if (!params.length && !f.returnType) {
        return f.compileStatus === 'Error' ? '(คอมไพล์ไม่ผ่าน — ยังไม่ทราบลายเซ็น)' : '(ยังไม่ได้คอมไพล์)'
      }
      const args = params
        .map((p) => `${this.csType(p.type)} ${p.name}${p.required ? '' : ` = ${this.literalOf(p.default)}`}`)
        .join(', ')
      return `${this.csType(f.returnType)} Run(${args})`
    },
    /**
     * INPUT-side counterpart of the fieldKey chip (which is the OUTPUT): how many of this formula's
     * parameters already know where to read from. Returns null when nothing is bound, so the chip is absent
     * on rows nobody has wired up rather than printing `0/3` on every line.
     */
    bindingSummaryOf(f) {
      const entries = (Array.isArray(f.parameterBindings) ? f.parameterBindings : []).filter((b) => b && b.name)
      if (!entries.length) return null
      const sourceByName = new Map(entries.map((b) => [String(b.name), String(b.source || '').toUpperCase()]))
      const params = Array.isArray(f.parameters) ? f.parameters : []
      // Count against the REAL parameters whenever they are known: a binding left behind by an older
      // signature is kept on purpose (never auto-deleted) and must not inflate the ratio.
      const matched = params.length
        ? params.map((p) => sourceByName.get(String(p.name))).filter((s) => s !== undefined)
        : [...sourceByName.values()]
      const fields = matched.filter((s) => s === 'FIELD').length
      // `required` means "no default in the source", so this is the combination that fails at run time.
      const missing = params.filter((p) => p.required && !sourceByName.has(String(p.name))).length
      return {
        bound: matched.length,
        total: params.length || sourceByName.size,
        color: missing > 0 ? 'warning' : 'info',
        hint: [
          `จาก Tax Config ${fields} ตัว`,
          matched.length - fields > 0 ? `ค่าคงที่ ${matched.length - fields} ตัว` : '',
          missing > 0 ? `ยังไม่ผูกและไม่มีค่า default ${missing} ตัว — สูตรนี้จะรันไม่ได้` : '',
        ].filter(Boolean).join(' · '),
      }
    },
    /** Compiled (green) / Error N (red) / Draft (grey) — the caller greys it out when isStale. */
    healthOf(f) {
      if (f.compileStatus === 'Compiled') {
        return { color: 'success', icon: 'mdi-check-circle-outline', text: 'Compiled' }
      }
      if (f.compileStatus === 'Error') {
        const n = Number(f.compileErrorCount) || 0
        return { color: 'error', icon: 'mdi-alert-circle-outline', text: n > 0 ? `Error ${n}` : 'Error' }
      }
      return { color: 'grey', icon: 'mdi-circle-outline', text: 'Draft' }
    },
    /**
     * 🔴 `parseUtcDate` ไม่ใช่ `new Date` — ค่าบน wire เป็น UTC ที่ไม่มี `Z` ต่อท้าย จึงถูก parse เป็นเวลา
     * ท้องถิ่นและโชว์ช้าไป 7 ชม. (ดู `utils/thaiTime.js`) · คง `'th-TH'` + `timeStyle` เดิมไว้ทั้งหมด
     * เปลี่ยนแค่ให้ format ที่โซนไทย ⇒ หน้าตาเดิม (รวมปี พ.ศ.) แต่ตัวเลขถูก
     */
    formatDate(value) {
      const d = parseUtcDate(value)
      if (!d) return value ? String(value) : '—'
      return d.toLocaleString('th-TH', { dateStyle: 'medium', timeStyle: 'short', timeZone: THAI_TIME_ZONE })
    },

    // ── create ──────────────────────────────────────────────────────────────
    openCreate() {
      this.createDialog = { open: true, functionCode: '', name: '', description: '', fieldKey: '' }
      // Not awaited: the dialog opens immediately and the combobox shows its own loading state. Cached
      // after the first success, so re-opening costs nothing.
      this.loadFieldRules()
    },
    /**
     * The FieldKey registry of this page's Tax Config version (route param) — the same call the question
     * item dialog and the C# editor make.
     *
     * 🔴 Fails soft on EVERY failure mode (no configVersionId, 4xx/5xx, empty registry): the picker is a
     * convenience over a free-text field, so a failed fetch must leave the author able to type a key and
     * create the function exactly as before — it only loses the suggestions.
     */
    async loadFieldRules() {
      // Cached: the registry belongs to a fixed config version, and this page cannot edit it.
      if (this.fieldRulesLoaded || this.loadingFieldRules) return
      const cid = this.configVersionId
      if (cid === null || cid === undefined || String(cid).trim() === '') {
        this.fieldRules = []
        this.fieldRulesError = 'ไม่ทราบ Tax Config version ของหน้านี้ — เลือกจากรายการไม่ได้ (พิมพ์ FieldKey เองได้)'
        return
      }
      this.loadingFieldRules = true
      this.fieldRulesError = ''
      try {
        const rules = await taxConfigV2Api.getRules(cid, null, true)
        this.fieldRules = (rules || [])
          .map((r) => ({
            fieldKey: String((r && (r.fieldKey ?? r.FieldKey)) || '').trim(),
            label: String((r && (r.label ?? r.Label)) || ''),
            valueType: String((r && (r.valueType ?? r.ValueType)) || '').trim().toLowerCase(),
          }))
          .filter((r) => r.fieldKey)
        // Only a SUCCESS is cached, so a transient failure retries the next time the dialog opens.
        this.fieldRulesLoaded = true
      } catch (err) {
        this.fieldRules = []
        this.fieldRulesError = `โหลดรายการ FieldKey ไม่สำเร็จ (${(err && err.message) || 'ไม่ทราบสาเหตุ'}) — พิมพ์ FieldKey เองได้ตามปกติ`
      } finally {
        this.loadingFieldRules = false
      }
    },
    /**
     * 🔴 A combobox hands back a STRING when the key is typed and (depending on the item shape) an ITEM
     * when it is picked from the list — storing that object would post `[object Object]` as the FieldKey.
     * Everything is flattened to the verbatim key here.
     *
     * Trim is deliberately NOT applied on the way in: this fires on every keystroke, and trimming live
     * would eat the space the moment it is typed. `confirmCreate` trims what it sends, as it already did.
     * Case is left verbatim too — FieldKey is a runtime key, not an identifier this page may re-shape.
     */
    setCreateFieldKey(value) {
      this.createDialog.fieldKey = this.toFieldKeyString(value)
    },
    toFieldKeyString(value) {
      if (value === null || value === undefined) return ''
      if (typeof value === 'string') return value
      if (typeof value === 'object') return String(value.fieldKey ?? value.value ?? value.title ?? '')
      return String(value)
    },
    /** Search BOTH the key and the Thai label: admins remember "เงินได้ 40(1)", not INCOME_40_1. */
    filterFieldItem(_value, query, item) {
      const q = String(query || '').trim().toLowerCase()
      if (!q) return true
      const raw = (item && item.raw) || {}
      return `${raw.fieldKey || ''} ${raw.label || ''}`.toLowerCase().includes(q)
    },
    async confirmCreate() {
      if (!this.isCreateCodeValid) return
      this.creating = true
      try {
        const maxOrder = this.functions.reduce((m, f) => Math.max(m, Number(f.sortOrder) || 0), 0)
        // sourceCode intentionally omitted — the backend seeds a starter function that compiles, runs,
        // and demonstrates the parameter contract the run panel renders.
        const created = await taxCalculateV2Api.createFunction(this.calcVersionId, {
          functionCode: String(this.createDialog.functionCode || '').trim(),
          name: String(this.createDialog.name || '').trim(),
          description: String(this.createDialog.description || '').trim(),
          // Always a plain string: setCreateFieldKey flattens whatever the combobox emits, and this trims it.
          fieldKey: this.createFieldKey,
          sortOrder: maxOrder + 1,
          isActive: true,
        })
        if (!created || created.functionID == null) throw new Error('สร้างฟังก์ชันไม่สำเร็จ (ไม่ได้รับรหัสฟังก์ชัน)')
        this.createDialog.open = false
        this.showSnack('สร้างฟังก์ชันแล้ว — เปิดหน้าแก้ไขโค้ด', 'success')
        this.editFunction(created)
      } catch (err) {
        this.showSnack((err && err.message) || 'สร้างฟังก์ชันไม่สำเร็จ', 'error')
      } finally {
        this.creating = false
      }
    },

    // ── per-row ops (FunctionIDs must stay stable → never bulk-replace) ──────
    /**
     * Update body that preserves everything except the overridden fields. `sourceCode` is deliberately
     * NOT sent: omitting it makes the backend keep the stored source, and it is also what lets a
     * server-side rename rewrite the class declaration for us.
     */
    functionBody(f, override = {}) {
      return {
        functionCode: f.functionCode,
        name: f.name || '',
        description: f.description || '',
        fieldKey: f.fieldKey || '',
        sortOrder: Number(f.sortOrder) || 0,
        isActive: f.isActive !== false,
        // Echo the row's own parameter bindings back so a pure sort-order swap cannot drop them. Sent only
        // when the row actually carries the field: an API response without it means "this build does not
        // populate bindings", and inventing an empty list there would clear what is stored.
        ...(Array.isArray(f.parameterBindings) ? { parameterBindings: f.parameterBindings } : {}),
        ...override,
      }
    },
    async moveFunction(idx, delta) {
      const to = idx + delta
      if (to < 0 || to >= this.functions.length || this.busy) return
      const a = this.functions[idx]
      const b = this.functions[to]
      this.busy = true
      try {
        // Swap the two rows' sortOrder with TWO updates instead of a bulk save: a bulk replace would
        // delete+reinsert every function and regenerate the FunctionIDs the editor route links to —
        // and here the row's payload is SOURCE CODE, which people bookmark, diff and quote in tickets.
        const aOrder = Number(a.sortOrder) || 0
        const bOrder = Number(b.sortOrder) || 0
        // Equal orders carry no information to swap, so break the tie explicitly in the move direction.
        const [oa, ob] = aOrder === bOrder ? [bOrder + delta, bOrder] : [bOrder, aOrder]
        await taxCalculateV2Api.updateFunction(a.functionID, this.functionBody(a, { sortOrder: oa }))
        await taxCalculateV2Api.updateFunction(b.functionID, this.functionBody(b, { sortOrder: ob }))
        await this.loadAll()
      } catch (err) {
        this.showSnack((err && err.message) || 'ย้ายลำดับไม่สำเร็จ', 'error')
      } finally {
        this.busy = false
      }
    },
    async duplicateFunction(f) {
      this.busy = true
      try {
        // The list row carries no sourceCode (summary DTO), so read the full function first.
        const full = await taxCalculateV2Api.getFunction(f.functionID)
        const base = String(f.functionCode || 'Function')
        let code = `${base}_copy`
        let n = 2
        while (this.codeSet.has(code.toLowerCase())) { code = `${base}_copy${n}`; n += 1 }
        // Fresh objects, never the source row's own entries: the copy is about to be re-pointed at other
        // fields, and a shared reference would edit the original formula's bindings along with it.
        const from = (full && Array.isArray(full.parameterBindings)) ? full.parameterBindings : f.parameterBindings
        const bindings = (Array.isArray(from) ? from : []).map((b) => ({ ...b }))
        const created = await taxCalculateV2Api.createFunction(this.calcVersionId, {
          functionCode: code,
          name: f.name ? `${f.name} (สำเนา)` : `${base} (สำเนา)`,
          description: f.description || '',
          // TWO different things on a function look like "a FieldKey", and they copy in OPPOSITE directions.
          // Do not collapse them into one rule the next time this block is touched:
          //
          //   fieldKey          = OUTPUT. Which Tax Config V2 field this formula WRITES. It is a 1:1 claim on
          //                       that field, so a copy must not silently claim it too — cleared on purpose.
          //   parameterBindings = INPUT.  Which fields this formula READS. Reading is not exclusive, and
          //                       re-pointing an input is exactly why people duplicate: the spouse formula is
          //                       this formula with `income` moved from INCOME_40_1 to SPOUSE_INCOME_40_1
          //                       (35 SPOUSE_* fields exist). Dropping these would mean re-binding every
          //                       parameter by hand on every copy — so they are carried over.
          fieldKey: '',
          parameterBindings: bindings,
          sourceCode: this.renameClassIn((full && full.sourceCode) || '', base, code),
          sortOrder: (Number(f.sortOrder) || 0) + 1,
          isActive: f.isActive !== false,
        })
        await this.loadAll()
        // Say what was and was NOT carried over, because the next step is always "go re-point the inputs".
        const carried = bindings.length ? ` · คัดลอกการผูกค่า ${bindings.length} รายการมาด้วย` : ''
        this.showSnack(
          created && created.compileStatus === 'Error'
            ? `ทำสำเนาเป็น ${code} แล้ว — แต่คอมไพล์ไม่ผ่าน กรุณาเปิดแก้ไข${carried}`
            : `ทำสำเนาเป็น ${code} แล้ว (ไม่ได้คัดลอก FieldKey ผลลัพธ์)${carried}`,
          created && created.compileStatus === 'Error' ? 'warning' : 'success',
        )
      } catch (err) {
        this.showSnack((err && err.message) || 'ทำสำเนาไม่สำเร็จ', 'error')
      } finally {
        this.busy = false
      }
    },
    /**
     * Rewrite `public static class <old>` → `<new>` in a copied body. FunctionCode IS the class name,
     * so a copy that keeps the original declaration would not compile — this mirrors the backend's own
     * RenameClass (which only runs on update, never on create).
     */
    renameClassIn(source, previousCode, newCode) {
      if (!source || !previousCode) return source
      const escaped = previousCode.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')
      return source.replace(new RegExp(`(\\bclass\\s+)${escaped}\\b`, 'g'), `$1${newCode}`)
    },
    /** Blast radius first: what stops working the moment this row is gone. */
    askDelete(f) {
      const impacts = []
      if (f.fieldKey) {
        impacts.push(`ผูกอยู่กับ FieldKey “${f.fieldKey}” — ฟิลด์นี้จะไม่มีสูตรคำนวณในเวอร์ชันนี้`)
      }
      if (this.isActiveVersion) {
        impacts.push(`เวอร์ชัน ${this.versionCode} กำลังใช้งานอยู่ (ACTIVE) — มีผลกับการคำนวณจริงทันที`)
      }
      if (f.compileStatus === 'Compiled' && !f.isStale) {
        impacts.push('ฟังก์ชันนี้คอมไพล์ผ่านและใช้งานได้อยู่')
      }
      this.deleteDialog = { open: true, fn: f, impacts }
    },
    async confirmDelete() {
      const f = this.deleteDialog.fn
      if (!f) return
      this.busy = true
      try {
        await taxCalculateV2Api.deleteFunction(f.functionID)
        delete this.panels[f.functionID]
        this.deleteDialog.open = false
        await this.loadAll()
        this.showSnack('ลบฟังก์ชันแล้ว', 'success')
      } catch (err) {
        this.showSnack((err && err.message) || 'ลบไม่สำเร็จ', 'error')
      } finally {
        this.busy = false
      }
    },

    // ── inline test run ─────────────────────────────────────────────────────
    /**
     * One input field per parameter. `required` means "no default", so an optional parameter shows its
     * default as the placeholder and may be left blank — the backend then binds the default itself.
     */
    fieldsFor(f) {
      const params = Array.isArray(f.parameters) ? f.parameters : []
      return params.map((p) => {
        const type = String(p.type || '')
        let kind = 'text'
        if (/^(decimal|double|int|single|float|long|short|byte|uint|sbyte)/i.test(type)) kind = 'number'
        else if (/^bool/i.test(type)) kind = 'boolean'
        return {
          name: p.name,
          type,
          kind,
          required: !!p.required,
          default: p.default,
          placeholder: p.required ? 'ต้องระบุ' : `ค่าเริ่มต้น ${this.literalOf(p.default)}`,
        }
      })
    },
    fieldLabel(fld) {
      return `${fld.name} (${this.csType(fld.type)})${fld.required ? ' *' : ''}`
    },
    /** Re-derive every open panel's fields after a reload, keeping the values the user typed. */
    syncPanels() {
      const alive = new Set(this.functions.map((f) => f.functionID))
      Object.keys(this.panels).forEach((key) => {
        if (!alive.has(Number(key))) delete this.panels[key]
      })
      this.functions.forEach((f) => {
        const panel = this.panels[f.functionID]
        if (!panel) return
        panel.fields = this.fieldsFor(f)
        panel.values = this.seedValues(panel.fields, panel.values)
      })
    },
    /** Blank values for new parameters; anything already typed is kept verbatim. */
    seedValues(fields, previous) {
      const values = {}
      fields.forEach((fld) => {
        const prev = previous ? previous[fld.name] : undefined
        if (prev !== undefined) { values[fld.name] = prev; return }
        values[fld.name] = fld.kind === 'boolean' ? Boolean(fld.default) : ''
      })
      return values
    },
    toggleRun(f) {
      const panel = this.panels[f.functionID]
      if (panel && panel.open) {
        panel.open = false
        return
      }
      const fields = this.fieldsFor(f)
      this.panels[f.functionID] = {
        open: true,
        loading: false,
        error: null,
        result: null,
        fields,
        values: this.seedValues(fields, panel && panel.values),
      }
    },
    closeRun(f) {
      const panel = this.panels[f.functionID]
      if (panel) panel.open = false
    },
    /**
     * Argument object keyed by parameter NAME. A blank optional field is OMITTED so the backend binds
     * the compiled default; a blank REQUIRED field is also omitted on purpose — the backend answers
     * with errorKind 'binding' + errorParameter, which this panel paints onto that exact input.
     */
    buildArguments(panel) {
      const args = {}
      panel.fields.forEach((fld) => {
        const raw = panel.values[fld.name]
        if (fld.kind === 'boolean') { args[fld.name] = Boolean(raw); return }
        if (raw === '' || raw === null || raw === undefined) return
        if (fld.kind === 'number') {
          const n = Number(raw)
          args[fld.name] = Number.isFinite(n) ? n : raw
          return
        }
        args[fld.name] = String(raw)
      })
      return args
    },
    async runOne(f) {
      const panel = this.panels[f.functionID]
      if (!panel) return
      panel.loading = true
      panel.error = null
      panel.result = null
      try {
        // A compile/binding/timeout/runtime failure comes back as HTTP 200 with ok:false — branch on
        // the payload, and let `catch` handle only a real transport failure.
        const res = await taxCalculateV2Api.runFunction(f.functionID, { arguments: this.buildArguments(panel) })
        if (res && res.ok) panel.result = res
        else panel.error = res || { errorKind: 'runtime', errorMessage: 'รันไม่สำเร็จ' }
      } catch (err) {
        panel.error = { errorKind: 'runtime', errorMessage: (err && err.message) || 'เรียก API ไม่สำเร็จ' }
      } finally {
        panel.loading = false
      }
    },
    /** True when the last run blamed THIS parameter (errorKind 'binding'). */
    isErrorParam(f, fld) {
      const panel = this.panels[f.functionID]
      return !!(panel && panel.error && panel.error.errorParameter === fld.name)
    },
    errorKindLabel(kind) {
      if (kind === 'compile') return 'คอมไพล์ไม่ผ่าน'
      if (kind === 'binding') return 'ค่าพารามิเตอร์ไม่ถูกต้อง'
      if (kind === 'timeout') return 'ใช้เวลานานเกินกำหนด'
      if (kind === 'runtime') return 'เกิดข้อผิดพลาดขณะรัน'
      return 'รันไม่สำเร็จ'
    },
    resultText(res) {
      const v = res ? res.result : null
      if (v === null || v === undefined) return 'null'
      if (typeof v === 'number') return v.toLocaleString('th-TH', { maximumFractionDigits: 6 })
      if (typeof v === 'boolean') return v ? 'true' : 'false'
      if (typeof v === 'object') {
        try { return JSON.stringify(v) } catch (e) { return String(v) }
      }
      return String(v)
    },

    // ── Save history ────────────────────────────────────────────────────────
    /**
     * Nothing to gate here (unlike the question builder): this page has no unsaved buffer — every
     * reorder / delete / clone commits immediately, so opening history can never lose an edit.
     */
    openHistory() {
      if (this.calcVersionId == null) return
      this.historyDialog = true
    },
    /**
     * The dialog already restored (and reloaded its own table). What is left here is to make the
     * page agree with the database: a restore can ADD rows back, REMOVE rows, rewrite source and
     * re-compile — sort order and compile health included — so nothing short of a full reload is
     * honest. Only then does the page speak, ONCE: a success call followed by a warning call would
     * just flash the first one away.
     */
    async onHistoryRestored(result) {
      await this.loadAll()

      const from = result && result.restoredFromNo
      const label = from ? `กู้คืนจาก v${from} เรียบร้อย` : 'กู้คืนเวอร์ชันเรียบร้อย'
      const count = Number(
        result && result.functionCount != null ? result.functionCount : this.functions.length,
      )
      const warns = result && Array.isArray(result.warnings) ? result.warnings : []

      if (this.loadError) {
        // The restore itself committed on the server; only the re-sync failed.
        this.showSnack(`${label} แต่โหลดรายการใหม่ไม่สำเร็จ — กรุณารีเฟรชหน้า`, 'error')
      } else if (warns.length) {
        this.showSnack(`${label} (${count} ฟังก์ชัน) — ${warns.join(' | ')}`, 'warning')
      } else {
        this.showSnack(`${label} (${count} ฟังก์ชัน)`, 'success')
      }
    },

    showSnack(message, color) {
      this.snackbar = { show: true, color: color || 'success', message }
    },
  },
}
</script>

<style scoped>
.tcv2-page {
  display: flex;
  flex-direction: column;
  /* padding-top is set at runtime by updateLayoutInsets() to clear the fixed breadcrumb bar;
     border-box keeps that padding inside min-height so the page gains no phantom scroll. */
  box-sizing: border-box;
  height: calc(100vh - 64px);
  overflow: hidden;
  background: rgb(var(--v-theme-background));
}
.tcv2-title { font-weight: 600; }
.tcv2-sub { opacity: 0.85; font-weight: 400; font-size: 0.9rem; }
.tcv2-text-primary { color: rgb(var(--v-theme-primary)) !important; }

.tcv2-center {
  flex: 1;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 48px;
}

.tcv2-head {
  display: flex;
  align-items: center;
  gap: 4px;
  padding: 12px 20px 0;
  min-width: 0;
}
.tcv2-head-code {
  font-family: 'JetBrains Mono', 'Roboto Mono', monospace;
  font-size: 0.86rem;
  color: rgb(var(--v-theme-primary));
  background: rgba(var(--v-theme-primary), 0.1);
  padding: 1px 7px;
  border-radius: 5px;
  flex: none;
}
.tcv2-head-desc {
  font-size: 0.82rem;
  color: rgba(var(--v-theme-on-surface), 0.6);
  margin-left: 8px;
  flex: 1 1 0;
  min-width: 0;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.tcv2-actions {
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 12px 20px 4px;
}
.tcv2-hint {
  font-size: 0.78rem;
  color: rgba(var(--v-theme-on-surface), 0.55);
  display: inline-flex;
  align-items: center;
  gap: 4px;
}

.tcv2-list { padding: 10px 20px 28px; flex: 1 1 auto; min-height: 0; overflow-y: scroll;
  scrollbar-width: thin; scrollbar-color: rgba(25, 118, 210, 0.3) rgba(0, 0, 0, 0.05); }
/* Same thin blue scrollbar as the other Tax Planning tables (TaxPlanningMobileConfigurations.vue) */
.tcv2-list::-webkit-scrollbar { width: 6px; }
.tcv2-list::-webkit-scrollbar-track { background: rgba(0, 0, 0, 0.05); }
.tcv2-list::-webkit-scrollbar-thumb { background: rgba(25, 118, 210, 0.3); border-radius: 3px; }
.tcv2-list::-webkit-scrollbar-thumb:hover { background: rgba(25, 118, 210, 0.5); }

.tcv2-row {
  margin-bottom: 10px;
  border-radius: 12px;
  cursor: pointer;
  transition: border-color 0.15s ease, box-shadow 0.15s ease;
}
.tcv2-row:hover {
  border-color: rgb(var(--v-theme-primary));
  box-shadow: 0 2px 10px rgba(var(--v-theme-primary), 0.14);
}
.tcv2-row-main { display: flex; align-items: center; gap: 12px; padding: 10px 14px; min-width: 0; }

.tcv2-order { display: flex; flex-direction: column; align-items: center; min-width: 34px; flex: none; }
.tcv2-order-num { font-size: 0.72rem; color: rgba(var(--v-theme-on-surface), 0.55); line-height: 1; }

.tcv2-row-body { flex: 1; min-width: 0; }
.tcv2-row-head { display: flex; align-items: center; flex-wrap: wrap; gap: 2px; }
.tcv2-code {
  font-family: 'JetBrains Mono', 'Roboto Mono', monospace;
  font-size: 0.86rem;
  font-weight: 600;
  color: rgb(var(--v-theme-primary));
  background: rgba(var(--v-theme-primary), 0.1);
  padding: 0 6px;
  border-radius: 5px;
}
.tcv2-name { font-weight: 600; font-size: 0.95rem; margin-left: 8px; }
.tcv2-desc {
  font-size: 0.76rem;
  color: rgba(var(--v-theme-on-surface), 0.5);
  margin-left: 8px;
  /* flex child: min-width:0 is what actually lets it shrink and ellipsize instead of overflowing */
  flex: 1 1 0;
  min-width: 0;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}
.tcv2-row-sig { display: flex; align-items: baseline; gap: 6px; margin-top: 4px; min-width: 0; }
.tcv2-sig-mark { font-weight: 700; color: rgb(var(--v-theme-primary)); flex: none; font-style: italic; }
.tcv2-sig {
  font-family: 'JetBrains Mono', 'Roboto Mono', monospace;
  font-size: 0.84rem;
  color: rgba(var(--v-theme-on-surface), 0.82);
  flex: 1 1 0;
  min-width: 0;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}
.tcv2-row-meta {
  margin-top: 4px;
  font-size: 0.72rem;
  color: rgba(var(--v-theme-on-surface), 0.5);
  display: flex;
  align-items: center;
  gap: 4px;
  flex-wrap: wrap;
}
/* never let the long description/signature squeeze the actions — they keep their intrinsic width */
.tcv2-row-actions { display: flex; align-items: center; gap: 4px; flex: none; }

/* create dialog — FieldKey picker */
.tcv2-opt-label { font-size: 0.78rem; color: rgba(var(--v-theme-on-surface), 0.6); }
.tcv2-create-warn {
  display: flex;
  align-items: flex-start;
  gap: 4px;
  margin-top: 8px;
  font-size: 0.76rem;
  line-height: 1.35;
  color: rgb(var(--v-theme-warning));
}
.tcv2-create-warn code {
  font-family: 'JetBrains Mono', 'Roboto Mono', monospace;
  background: rgba(var(--v-theme-warning), 0.12);
  padding: 0 4px;
  border-radius: 4px;
}

.tcv2-run {
  margin: 0 14px 12px;
  padding: 12px;
  border-radius: 10px;
  background: rgba(var(--v-theme-primary), 0.05);
  border: 1px dashed rgba(var(--v-theme-primary), 0.35);
  cursor: default;
}
.tcv2-run-head { display: flex; align-items: center; margin-bottom: 8px; font-size: 0.9rem; }
.tcv2-run-hint { color: rgba(var(--v-theme-on-surface), 0.55); margin-left: 6px; }
.tcv2-run-fields { display: flex; flex-wrap: wrap; gap: 10px; align-items: center; }
.tcv2-run-field { max-width: 260px; min-width: 190px; }
.tcv2-run-check { max-width: 260px; min-width: 190px; flex: none; }
.tcv2-run-errparam { margin-top: 4px; font-size: 0.82rem; }
.tcv2-run-diag {
  margin-top: 3px;
  font-size: 0.8rem;
  font-family: 'JetBrains Mono', 'Roboto Mono', monospace;
  white-space: pre-wrap;
  word-break: break-word;
}
.tcv2-run-detail {
  margin-top: 6px;
  font-size: 0.78rem;
  opacity: 0.8;
  white-space: pre-wrap;
  word-break: break-word;
}
.tcv2-run-result { margin-top: 10px; }
.tcv2-run-value { font-size: 1.05rem; }
.tcv2-run-value-label { color: rgba(var(--v-theme-on-surface), 0.7); }
.tcv2-run-value strong { color: rgb(var(--v-theme-primary)); font-size: 1.25rem; margin-left: 6px; }
.tcv2-run-stats {
  margin-top: 3px;
  font-size: 0.78rem;
  color: rgba(var(--v-theme-on-surface), 0.55);
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 2px;
}
</style>
